import { useEffect, useState } from 'react';

import { useConsultationContext } from '../../../context/ConsultationContext';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  getPrescription,
  getPrescriptionDownloadLinks,
  getSickLeave,
  getSickLeaveDownloadLinks,
  postPrescription,
  postSickLeave,
} from '../../../store/slices/sickLeaveAndPrescription';
import {
  IPerson,
  selectDirtyState,
  selectMerchantPersonId,
  selectPersonLoggedIn,
  setDirtyState,
} from '../../../store/slices/auth';
import { ContentViewer } from '../../ContentViewer/ContentViewer';
import { mainHostUrl } from '../../../helpers';
import { IDigitalHealthProfile } from '../../UI/SelectedSalesOrder/DigitalHealthProfileTab';
import Modal from '../../UI/Modals/Modal';
import PrimaryButtonV2 from '../../UI/Buttons/PrimaryButtonV2';
import WithLoader from '../../WithLoader/WithLoader';
import styles from './SaveTextArea.module.scss';
import { useTranslation } from '../../../hooks/useTranslations';
import { toast } from 'react-toastify';
import {
  CHARACTER_LIMITS,
  characterLimitMessage,
  exceedsCharacterLimit,
} from '../../../utils/characterLimits';

interface OnlineResult extends IDigitalHealthProfile {
  sales_order_id: string;
  sales_order_item_id: string;
  work_order_id: string;
  work_order_status_id: string;
  person_account_number: string;
  product_order_id_out: string;
}

interface SaveTextAreaProps {
  type: 'sickLeave' | 'prescription';
  selectedConsultation?: OnlineResult;
}

export default function SaveTextArea({ type, selectedConsultation }: SaveTextAreaProps) {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    sickLeaveState,
    setSickLeaveState,
    prescriptionState,
    setPrescriptionState,
    selectedSalesOrder,
    deleteTab,
    addTab,
  } = useConsultationContext();
  let isReports = window.location.pathname.includes('reports');

  const isDirtyState = useAppSelector(selectDirtyState);
  const merchantPersonId = useAppSelector<string>(selectMerchantPersonId);
  const personLoggedIn = useAppSelector<IPerson>(selectPersonLoggedIn);
  const sickLeaveAndPrescription = useAppSelector<any>((state) => state.sickLeaveAndPrescription);
  const [docToDownload, setDocToDownload] = useState<any>(null);
  const check =
    type === 'sickLeave'
      ? sickLeaveAndPrescription?.sickLeave
      : sickLeaveAndPrescription?.prescription;

  const [editTextArea, setEditTextArea] = useState<boolean>(
    check?.length === 0 || check === undefined
  );

  const workOrderStatusId =
    selectedConsultation?.work_order_status_id ||
    selectedSalesOrder?.work_order?.work_order_status_id;

  const externamSalesOrderItemId =
    selectedConsultation?.product_order_item_id_out ||
    selectedSalesOrder.sales_order_item.external_sales_order_item_id;

  const externamSalesOrderId =
    selectedConsultation?.product_order_id_out ||
    selectedSalesOrder.sales_order.sales_order_external_id;

  const workOrderId =
    selectedConsultation?.work_order_id || selectedSalesOrder?.work_order?.work_order_id;

  const taId = selectedConsultation?.ta_id || selectedSalesOrder.sales_order.ta_id;

  const personAccNum =
    selectedConsultation?.person_account_number ||
    selectedSalesOrder?.customer_person?.person_account_number;

  const handleText = (text: string) => {
    dispatch(setDirtyState({ isDirty: true, isModalOpen: false, component: 'saveTextArea' }));
    if (type === 'sickLeave') {
      return setSickLeaveState(text);
    } else {
      return setPrescriptionState(text);
    }
  };

  const handleSave = async (e: any) => {
    const value = type === 'sickLeave' ? sickLeaveState : prescriptionState;
    const limit = CHARACTER_LIMITS[type];

    if (exceedsCharacterLimit(value, limit)) {
      toast.error(characterLimitMessage(translations('error.character_limit'), limit), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    const data = {
      workOrderId: workOrderId ?? 0,
      merchantPersonId: parseFloat(merchantPersonId),
      personIdCreate: parseFloat(personLoggedIn.person_external_id),
    };

    if (type === 'sickLeave') {
      const resSickLeave = await dispatch(postSickLeave({ ...data, comment: sickLeaveState }));
      if (resSickLeave.meta.requestStatus === 'fulfilled') {
        dispatch(getSickLeave(workOrderId));
        setSickLeaveState('');
        setEditTextArea(false);
        dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'saveTextArea' }));
      }
    } else {
      const res = await dispatch(postPrescription({ ...data, comment: prescriptionState }));
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(getPrescription(workOrderId));
        setPrescriptionState('');
        setEditTextArea(false);
        dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'saveTextArea' }));
      }
    }
  };

  const handleDiscardChanges = (e: any) => {
    dispatch(setDirtyState({ isDirty: false, isModalOpen: false, component: 'saveTextArea' }));
    deleteTab(
      e,
      {
        value: type === 'sickLeave' ? 15 : 14,
        text: type === 'sickLeave' ? translations('sick_leave') : translations('prescription'),
        isVisible: true,
      },
      false
    );
  };

  const filterDocumentLink = (data: any) => {
    const doc = data.find((el: any) => {
      return (
        el.product_order_item_id === externamSalesOrderItemId &&
        el.ta_id === taId &&
        el.product_order_id === externamSalesOrderId
      );
    });
    return doc;
  };

  useEffect(() => {
    const fnc = async () => {
      if (type === 'sickLeave') {
        if (workOrderStatusId == 2) {
          const res = await dispatch(getSickLeaveDownloadLinks(personAccNum));
          if (res.meta.requestStatus === 'fulfilled') {
            setDocToDownload(filterDocumentLink(res.payload.data));
          }
        } else {
          dispatch(getSickLeave(workOrderId));
        }
      } else {
        if (workOrderStatusId == 2) {
          const res = await dispatch(getPrescriptionDownloadLinks(personAccNum));
          if (res.meta.requestStatus === 'fulfilled') {
            setDocToDownload(filterDocumentLink(res.payload.data));
          }
        } else {
          dispatch(getPrescription(workOrderId));
        }
      }
    };

    fnc();
  }, [type]);

  useEffect(() => {
    if (sickLeaveAndPrescription.status === 'fulfilled') {
      setEditTextArea(check?.length === 0 || check === undefined);
    }
  }, [sickLeaveAndPrescription.status]);

  const ContentViewerToRender = () => {
    if (check) {
      return (
        <div className={styles.container}>
          <ContentViewer
            title={type === 'sickLeave' ? translations('sick_leave') : translations('prescription')}
            content={
              type === 'sickLeave'
                ? sickLeaveAndPrescription?.sickLeave
                : sickLeaveAndPrescription?.prescription
            }
            editTab={() => {
              type === 'sickLeave'
                ? setSickLeaveState(sickLeaveAndPrescription.sickLeave)
                : setPrescriptionState(sickLeaveAndPrescription.prescription);
              setEditTextArea(true);
            }}
          />

          {type === 'prescription' && (
            <div className={styles.buttonWrapper} style={{ marginRight: '0px' }}>
              <button
                onClick={openPreview}
                className={`${styles.btn} ${styles.transparent}`}
                style={{ background: 'rgb(244, 247, 253)', minWidth: '150px', height: '40px' }}
              >
                {translations('preview')}
              </button>
            </div>
          )}
        </div>
      );
    }
    return '';
  };

  const openPreview = () => {
    addTab({ value: 17, text: translations('preview'), isVisible: true });
  };

  return (
    <WithLoader isLoading={sickLeaveAndPrescription.status === 'pending'}>
      {isReports &&
        type === 'sickLeave' &&
        selectedSalesOrder?.work_order?.work_order_status_id === 2 &&
        !docToDownload && <div className="emptyState">{translations('label.empty_state')}</div>}
      {isReports &&
        type === 'prescription' &&
        selectedSalesOrder?.work_order?.work_order_status_id === 2 &&
        !docToDownload && <div className="emptyState">{translations('label.empty_state')}</div>}
      {Number(workOrderStatusId) !== 2 ? (
        editTextArea ? (
          <div className={styles.container}>
            <textarea
              className={styles.textArea}
              placeholder={
                type === 'sickLeave'
                  ? (translations('label.sick_leave_placeholder') as string)
                  : (translations('label.prescription_placeholder') as string)
              }
              value={type === 'sickLeave' ? sickLeaveState : prescriptionState}
              onChange={(e) => handleText(e.target.value)}
              disabled={workOrderStatusId == 2}
            />
            <div className={styles.buttonWrapper}>
              <button
                onClick={handleSave}
                className={`${styles.btn} ${styles.blue}`}
                disabled={workOrderStatusId == 2}
                style={{ minWidth: '150px', height: '40px' }}
              >
                {translations('save')}
              </button>
              {type === 'prescription' && (
                <button
                  onClick={openPreview}
                  className={`${styles.btn} ${styles.transparent}`}
                  style={{ background: 'rgb(244, 247, 253)', minWidth: '150px', height: '40px' }}
                >
                  {translations('preview')}
                </button>
              )}
            </div>
          </div>
        ) : (
          ContentViewerToRender()
        )
      ) : (
        <>
          {docToDownload && (
            <div className={styles.container}>
              <div style={{ padding: '20px' }}>
                <a
                  target="_blank"
                  style={{ color: '#12143e' }}
                  href={mainHostUrl(docToDownload?.attachments?.[0].link)}
                >
                  {type === 'sickLeave'
                    ? translations('label.download.sickleave')
                    : translations('label.download.prescription')}
                </a>
              </div>
            </div>
          )}
        </>
      )}
      <Modal
        isOpen={isDirtyState.isModalOpen}
        onClose={() =>
          dispatch(setDirtyState({ isDirty: true, isModalOpen: false, component: 'saveTextArea' }))
        }
        containerPadding={'40px 0px'}
        hideCloseButton
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginTop: 10 }}>Save your changes before leaving?</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
          <PrimaryButtonV2
            text={translations('save')}
            containerStyle={{ marginRight: 20 }}
            buttonStyle={{ height: 40, width: 130 }}
            onClick={handleSave}
            disabled={false}
          />
          <PrimaryButtonV2
            text={translations('close')}
            invertedColors
            buttonStyle={{ height: 40, width: 130 }}
            onClick={handleDiscardChanges}
            disabled={false}
          />
        </div>
      </Modal>
    </WithLoader>
  );
}
